/**
 * Texts picker block for CreativeColumn.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, EyeOff, Filter, Plus, Search, Trash2 } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';
import type { AiTextMetadataPayload } from '@/hooks';
import { computeSmswiserTemplateId } from '@/utils/smswiser-template-id';
import { LeadsListTags, NO_TAGS_VALUE, TagFilterDropdown } from '@/features/smswiser-campaigns/components/shared';
import { AiGenerateDropdown } from '@/features/campaign-texts/components/AiGenerateDropdown';
import { GeneratedTextsPreview } from '@/features/campaign-texts/components/GeneratedTextsPreview';
import { GeneratedTextsPreviewPerDrop } from '@/features/campaign-texts/components/GeneratedTextsPreviewPerDrop';
import { useAiTextGeneration } from '@/features/smswiser-campaigns/components/ScaleDropModal/modals/scale-or-edit/creative/useAiTextGeneration';
import {
  isSmsTextOverLimit,
  SMS_TEXT_MAX_LENGTH,
  smsEffectiveGsmLength,
} from '@/features/campaign-texts/smsTextLimit';

import {
  MAX_SELECTED_TEXTS,
  parseSmsTextsInput,
  uniqueKeepOrderStrings,
} from '../../shared/PrepareNewDropsForm.utils';
import {
  smsTextsOverLengthMessage,
  smsTextsOverLengthUiState,
} from '../../shared/sms-texts-over-length.utils';

interface TextsPickerBlockProps {
  countryKey: string;
  smsTexts: string[];
  smsTextsWithIds?: Array<{ text: string; templateId: string }>;
  selectedSmsTexts: string[];
  textSearch: string;
  setTextSearch: (v: string) => void;
  showAddText: boolean;
  setShowAddText: (v: boolean | ((p: boolean) => boolean)) => void;
  newTextValue: string;
  setNewTextValue: (v: string) => void;
  newTextError: string | null;
  setNewTextError: (v: string | null) => void;
  onAddTexts: (texts: string[], aiTextMetadata?: AiTextMetadataPayload[]) => Promise<void>;
  onAddTextsDeselected?: (texts: string[], aiTextMetadata?: AiTextMetadataPayload[]) => Promise<void>;
  onSaveGeneratedAndSelectOnly?: (texts: string[]) => void;
  onPerDropSmsTextsApplied?: (batches: string[][]) => void;
  onAiGeneratedDeselectOld?: () => void;
  onDeleteText: (text: string) => Promise<void>;
  onDeleteTextsBulk?: (smsTexts: string[]) => Promise<void>;
  onMarkTextsForHide?: (texts: string[]) => void;
  pendingHideTexts?: string[];
  maxTexts?: number;
  setSmsTexts: (updater: (prev: string[]) => string[]) => void;
  setSelectedSmsTexts: (updater: (prev: string[]) => string[]) => void;
  tagsByTextId?: Record<string, string[]>;
  tagColors?: Record<string, string>;
  isBusy: boolean;
  dropCount?: number;
  dropLabels?: Array<{ id: string; label: string }>;
  /** Called when AI texts preview is shown/hidden so parent can adjust layout. */
  onAiTextsActive?: (active: boolean) => void;
}

export const TextsPickerBlock: FC<TextsPickerBlockProps> = ({
  countryKey,
  smsTexts,
  smsTextsWithIds,
  selectedSmsTexts,
  textSearch,
  setTextSearch,
  showAddText,
  setShowAddText,
  newTextValue,
  setNewTextValue,
  newTextError,
  setNewTextError,
  onAddTexts,
  onAddTextsDeselected: _onAddTextsDeselected,
  onSaveGeneratedAndSelectOnly,
  onPerDropSmsTextsApplied,
  onAiGeneratedDeselectOld,
  onDeleteText,
  onDeleteTextsBulk,
  onMarkTextsForHide,
  pendingHideTexts,
  maxTexts,
  setSmsTexts,
  setSelectedSmsTexts,
  tagsByTextId,
  tagColors,
  isBusy,
  dropCount = 1,
  dropLabels,
  onAiTextsActive,
}) => {
  const { hasPermission } = useAuth();
  const { success: toastSuccess } = useToast();
  const canGenerateAi = hasPermission('campaign_texts.generate');
  const [textTagFilter, setTextTagFilter] = useState<string>('');

  const onSavePerDropFlatten = useCallback(
    async (_batches: string[][]) => {
      // no-op: the hook's handleSavePerDrop/handleSaveAndHidePerDrop
      // already calls onAddTexts before this callback.
    },
    [],
  );

  const ai = useAiTextGeneration({
    countryKey,
    onAddTexts,
    onSaveGeneratedAndSelectOnly,
    onMarkTextsForHide,
    onSaveGeneratedPerDrop: dropCount > 1 ? onSavePerDropFlatten : undefined,
    onPerDropSmsTextsApplied,
    onAiGeneratedDeselectOld,
  });

  const hasAiTexts = Boolean(
    (ai.perDropBatches && ai.perDropBatches.length > 0) || ai.generatedTexts.length > 0,
  );
  useEffect(() => {
    onAiTextsActive?.(hasAiTexts);
  }, [hasAiTexts, onAiTextsActive]);
  useEffect(() => () => onAiTextsActive?.(false), [onAiTextsActive]);

  const textsTemplateIds = useMemo(
    () => (smsTextsWithIds ? Object.fromEntries(smsTextsWithIds.map((t) => [t.text, t.templateId])) : undefined),
    [smsTextsWithIds],
  );

  const getTagsForText = useCallback(
    (text: string): string[] | undefined => {
      if (!tagsByTextId) return undefined;
      const templateId = textsTemplateIds?.[text] ?? computeSmswiserTemplateId(text);
      return tagsByTextId[templateId];
    },
    [tagsByTextId, textsTemplateIds],
  );

  const availableTextTags = useMemo(() => {
    if (!tagsByTextId) return [];
    const set = new Set<string>();
    for (const tags of Object.values(tagsByTextId)) {
      for (const t of tags ?? []) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagsByTextId]);

  const applyTextInput = useCallback(
    async (value: string) => {
      const { texts, error } = parseSmsTextsInput(value);
      if (error) {
        setNewTextError(error);
        return;
      }
      if (!texts.length) return;
      setNewTextValue('');
      setNewTextError(null);
      await onAddTexts(texts);
    },
    [onAddTexts, setNewTextValue, setNewTextError],
  );

  const importFromInput = useCallback(
    (pasted: string, e: React.ClipboardEvent) => {
      const { texts, error } = parseSmsTextsInput(pasted);
      if (error) {
        e.preventDefault();
        setNewTextError(error);
        return;
      }
      if (!texts.length) return;
      e.preventDefault();
      setNewTextValue('');
      setNewTextError(null);
      onAddTexts(texts).then(() => setShowAddText(false));
    },
    [onAddTexts, setNewTextValue, setNewTextError],
  );

  const pasteSmsLength = useMemo(() => smsTextsOverLengthUiState(newTextValue), [newTextValue]);

  const filtered = useMemo(() => {
    let list = smsTexts.filter((t) => t.toLowerCase().includes(textSearch.toLowerCase()));
    if (textTagFilter && tagsByTextId) {
      if (textTagFilter === NO_TAGS_VALUE) {
        list = list.filter((t) => {
          const templateId = textsTemplateIds?.[t] ?? computeSmswiserTemplateId(t);
          return !tagsByTextId[templateId]?.length;
        });
      } else {
        const q = textTagFilter.toLowerCase();
        list = list.filter((t) => {
          const templateId = textsTemplateIds?.[t] ?? computeSmswiserTemplateId(t);
          return tagsByTextId[templateId]?.some((tag) => tag.toLowerCase() === q);
        });
      }
    }
    return [...list].sort((a, b) => {
      const aSel = selectedSmsTexts.includes(a);
      const bSel = selectedSmsTexts.includes(b);
      if (aSel === bSel) return 0;
      return aSel ? -1 : 1;
    });
  }, [smsTexts, textSearch, textTagFilter, tagsByTextId, textsTemplateIds, selectedSmsTexts]);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
      <label className="text-xs font-semibold text-muted-foreground">
        Campaign texts ({smsTexts.length}{maxTexts ? `/${maxTexts}` : ''})
        <span className={cn('ml-1 font-normal', selectedSmsTexts.length > MAX_SELECTED_TEXTS && 'text-destructive font-semibold')}>
          · {selectedSmsTexts.length}/{MAX_SELECTED_TEXTS} selected
        </span>
      </label>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-border/60 bg-muted/5">
        <div className="flex flex-col gap-2 border-b border-border/60 px-2 py-2">
          <div className="relative min-w-0">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-2 text-xs outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring placeholder:text-muted-foreground"
              placeholder="Filter texts..."
              value={textSearch}
              onChange={(e) => setTextSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
          {availableTextTags.length > 0 && (
            <div className="flex shrink-0 items-center gap-2">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <TagFilterDropdown
                value={textTagFilter}
                options={availableTextTags}
                tagColors={tagColors}
                displayLabel={
                  textTagFilter === ''
                    ? 'All tags'
                    : textTagFilter === NO_TAGS_VALUE
                      ? 'No tags'
                      : textTagFilter
                }
                onSelect={setTextTagFilter}
                placeholder="All tags"
                compact
              />
            </div>
          )}
          {filtered.length > 0 && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 text-xs"
                onClick={() => {
                  const capped = Math.min(filtered.length, MAX_SELECTED_TEXTS);
                  const isDeselectMode = selectedSmsTexts.length >= capped && selectedSmsTexts.length > 0;
                  if (isDeselectMode) {
                    setSelectedSmsTexts((prev) => prev.filter((t) => !filtered.includes(t)));
                  } else {
                    setSelectedSmsTexts((prev) =>
                      uniqueKeepOrderStrings([...prev, ...filtered])
                        .filter((t) => smsTexts.includes(t))
                        .slice(0, MAX_SELECTED_TEXTS),
                    );
                  }
                }}
                aria-label={
                  selectedSmsTexts.length >= Math.min(filtered.length, MAX_SELECTED_TEXTS) && selectedSmsTexts.length > 0
                    ? 'Deselect all filtered texts'
                    : 'Select all filtered texts'
                }
              >
                {selectedSmsTexts.length >= Math.min(filtered.length, MAX_SELECTED_TEXTS) && selectedSmsTexts.length > 0 ? 'Deselect all' : 'Select all'}
              </Button>
              {selectedSmsTexts.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 shrink-0 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const toDelete = [...selectedSmsTexts];
                    const deleteSet = new Set(toDelete);
                    if (onDeleteTextsBulk) await onDeleteTextsBulk(toDelete);
                    else for (const t of toDelete) await onDeleteText(t);
                    setSmsTexts((prev) => prev.filter((v) => !deleteSet.has(v)));
                    setSelectedSmsTexts(() => []);
                    toastSuccess('Deleted', `${toDelete.length} text${toDelete.length > 1 ? 's' : ''} removed`);
                  }}
                  disabled={isBusy}
                  aria-label={`Delete ${selectedSmsTexts.length} selected text(s)`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          )}
          {canGenerateAi && (
            <AiGenerateDropdown
              countryKey={countryKey}
              dropCount={dropCount}
              dropLabels={dropLabels}
              existingBatches={
                ai.perDropBatches?.length === dropCount ? ai.perDropBatches : undefined
              }
              onGenerated={ai.handleAiGenerated}
            />
          )}
          {smsTexts.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={() => setShowAddText((v) => !v)}
              aria-expanded={showAddText}
            >
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showAddText && 'rotate-180')} />
              New text
            </Button>
          )}
          </div>
        </div>

        {(() => {
          const hasAiGenerated = Boolean(
            (ai.perDropBatches && ai.perDropBatches.length > 0) || ai.generatedTexts.length > 0,
          );
          return (
            <>
              <div className={cn(hasAiGenerated && 'flex min-h-0 flex-1 flex-col overflow-hidden')}>
                <AnimatePresence>
                  {ai.perDropBatches && ai.perDropBatches.length > 0 ? (
                    <GeneratedTextsPreviewPerDrop
                      batches={ai.perDropBatches}
                      translationsPerDrop={ai.perDropTranslations}
                      groupIdsPerDrop={ai.groupIdsPerDrop}
                      onDevCopyExactPrompt={ai.handleDevCopyExactModelPrompt}
                      isDevCopyExactPromptPending={ai.isDevCopyExactPromptPending}
                      dropLabels={dropLabels}
                      onSave={ai.handleSavePerDrop}
                      onDismiss={ai.handleDismissPerDrop}
                      onRegenerate={ai.handleRegeneratePerDrop}
                      onRegenerateAll={ai.handleRegenerateAllPerDrop}
                      onRegenerateOverLength={ai.handleRegenerateOverLengthPerDrop}
                      onRegenerateFailed={ai.handleRegenerateFailedPerDrop}
                      onSaveAndHide={ai.handleSaveAndHidePerDrop}
                      onTranslatePerDropAll={ai.handleTranslatePerDropAll}
                      onTranslatePerDropMissing={ai.handleTranslatePerDropMissing}
                      onTranslateAllPerDrop={ai.handleTranslateAllPerDrop}
                      isRegenerating={ai.isRegenerating}
                      isTranslating={ai.isTranslating}
                      useAndHideOnly={dropCount > 1}
                      applied={ai.perDropApplied}
                      generationTimeMs={ai.perDropGenerationTimeMs}
                      fillHeight={hasAiGenerated}
                    />
                  ) : ai.generatedTexts.length > 0 ? (
                    <GeneratedTextsPreview
                      texts={ai.generatedTexts}
                      translations={ai.generatedTranslations}
                      onDevCopyExactPrompt={ai.handleDevCopyExactModelPrompt}
                      isDevCopyExactPromptPending={ai.isDevCopyExactPromptPending}
                      onSave={ai.handleSaveGenerated}
                      onSaveAndHide={onMarkTextsForHide ? ai.handleSaveAndHide : undefined}
                      onDismiss={ai.handleDismiss}
                      onRegenerate={ai.handleRegenerate}
                      onRegenerateOverLength={ai.handleRegenerateOverLength}
                      onTranslateAll={ai.handleTranslateAll}
                      onTranslateMissing={ai.handleTranslateMissing}
                      isRegenerating={ai.isRegenerating}
                      isTranslating={ai.isTranslating}
                      fillHeight={hasAiGenerated}
                    />
                  ) : null}
                </AnimatePresence>
              </div>
              {!hasAiGenerated && (showAddText || smsTexts.length === 0) && (
          <div className="border-b border-border p-2">
            <textarea
              className={cn(
                'min-h-[90px] w-full resize-none rounded-md border bg-background p-2 text-xs outline-none',
                pasteSmsLength.overLimit && 'border-destructive ring-2 ring-destructive/25',
              )}
              placeholder='Paste text or JSON (e.g. ["Text A","Text B"]) — JSON paste auto-adds'
              value={newTextValue}
              onChange={(e) => {
                setNewTextValue(e.target.value);
                setNewTextError(null);
              }}
              onPaste={(e) => {
                const pasted = e.clipboardData?.getData('text') ?? '';
                if (!pasted.trim()) return;
                const looksLikeJson = pasted.trimStart().startsWith('[') || pasted.trimStart().startsWith('{');
                if (looksLikeJson) importFromInput(pasted, e);
              }}
              disabled={isBusy}
            />
            {pasteSmsLength.overLimit ? (
              <p className="mt-1.5 text-xs font-medium text-destructive" role="status">
                {smsTextsOverLengthMessage(pasteSmsLength)}
              </p>
            ) : null}
            {newTextError ? (
              <p className="mt-1.5 text-xs text-destructive" role="alert">
                {newTextError}
              </p>
            ) : null}
            <div className="mt-2 flex items-center justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={async () => {
                  await applyTextInput(newTextValue);
                  if (smsTexts.length > 0) setShowAddText(false);
                }}
                disabled={!newTextValue.trim() || isBusy}
              >
                <Plus className="h-3 w-3" /> Add
              </Button>
            </div>
          </div>
        )}
              </>
            );
          })()}

        {!((ai.perDropBatches && ai.perDropBatches.length > 0) || ai.generatedTexts.length > 0) && (
        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-x-hidden overflow-y-auto p-1.5">
          {filtered.map((smsText, index) => {
            const isSelected = selectedSmsTexts.includes(smsText);
            const performanceTags = getTagsForText(smsText);
            const textTooLong = isSmsTextOverLimit(smsText);
            return (
              <div
                key={`${index}-${smsText.slice(0, 32)}`}
                data-testid="campaign-text-item"
                data-index={index}
                role="button"
                tabIndex={0}
                aria-label={`${isSelected ? 'Deselect' : 'Select'} SMS text${textTooLong ? ' (over 160 characters)' : ''}`}
                onClick={() =>
                  setSelectedSmsTexts((prev) =>
                    prev.includes(smsText) ? prev.filter((v) => v !== smsText)
                      : prev.length >= MAX_SELECTED_TEXTS ? prev : [...prev, smsText],
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedSmsTexts((prev) =>
                      prev.includes(smsText) ? prev.filter((v) => v !== smsText)
                        : prev.length >= MAX_SELECTED_TEXTS ? prev : [...prev, smsText],
                    );
                  }
                }}
                className={cn(
                  'group relative flex cursor-pointer items-start gap-2 rounded-md border px-2.5 py-1.5 pr-8 transition-all',
                  textTooLong && 'border-destructive/60 bg-destructive/5 ring-1 ring-destructive/20',
                  !textTooLong &&
                    (isSelected
                      ? 'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20'
                      : 'border-border/60 bg-muted/30 hover:border-border hover:bg-muted/50'),
                )}
              >
                <div className="min-w-0 flex-1 flex flex-col gap-1">
                  <span
                    className={cn(
                      'break-words text-sm font-normal leading-[1.2]',
                      textTooLong ? 'text-destructive' : 'text-foreground',
                    )}
                    data-testid="campaign-text-value"
                    title={textsTemplateIds?.[smsText] ? `Template ID: ${textsTemplateIds[smsText]}` : undefined}
                  >
                    {smsText}
                  </span>
                  {textTooLong ? (
                    <span className="text-[10px] font-semibold text-destructive">
                      {smsEffectiveGsmLength(smsText)}/{SMS_TEXT_MAX_LENGTH} effective GSM — SMSWiser max{' '}
                      {SMS_TEXT_MAX_LENGTH}
                    </span>
                  ) : null}
                  {pendingHideTexts?.includes(smsText) && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 font-medium">
                      <EyeOff className="h-2.5 w-2.5" /> Hides after draft
                    </span>
                  )}
                  {performanceTags?.length ? (
                    <LeadsListTags
                      performanceTags={performanceTags}
                      tagColors={tagColors}
                      compact
                    />
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 shrink-0 p-0 opacity-50 transition-opacity hover:opacity-100 hover:text-destructive group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteText(smsText).then(() => {
                      setSmsTexts((prev) => prev.filter((v) => v !== smsText));
                      setSelectedSmsTexts((prev) => prev.filter((v) => v !== smsText));
                      toastSuccess('Deleted', '1 text removed');
                    });
                  }}
                  disabled={isBusy}
                  aria-label="Delete sms text"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="w-full py-6 text-center text-xs text-muted-foreground">
              {textSearch || textTagFilter ? 'No texts match your filter.' : 'No texts yet — add one above.'}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};
